import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FaPlus, FaTimes } from 'react-icons/fa';
import styled from 'styled-components';
import { PATIENT_MODAL_CONFIG } from '../../utils/patient';
import { SERVICE_LINE_NAMES } from '../../config';
import LoadingComponent from './LoadingComponent';
import { PrimaryButton } from './Buttons';

import { logEvent } from '../../utils/logEvent';
import Modal from '../../containers/common/Modal';
import StyledTappable from '../../components/common/StyledTappable';
import { FlyInTransition } from '../../components/transitions/transitions';
import plusIcon from '../../images/pharmacy/plus.svg';
import homeIcon from '../../images/pharmacy/home.svg';
import crossIcon from '../../images/common/cross.svg';
import profileIcon from '../../images/lab-test/profile.svg';
import checkedIcon from '../../images/pharmacy/checked.svg';
import pencilIcon from '../../images/lab-test/pencil.svg';
import trashIcon from '../../images/pharmacy/trash.svg';
import editIcon from '../../images/pharmacy/edit.svg';

const AddTextIcon = styled(FaPlus)`
  color: var(--primary-button);
  height: 10px;
  width: 10px;
`;

const CloseContainer = styled(StyledTappable)`
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled(FaTimes)`
  color: #413958;
`;

const DisclaimerText = styled.div`
  background: #fff3dc;
  color: #585969;
  width: 100%;
  border-radius: 12px;
  padding: 10px;
  font-style: normal;
  font-size: 12px;
  font-weight: 400;
  line-height: 150%; /* 18px */
  display: inline-flex;
  gap: 5px;
`;

export const PatientModal = ({
  showPatient,
  selectedPatient: initialSelectedPatient,
  serviceLine,
  cartId,
  isConciergeBooking,
  hideModal: hideModalProp,
  selectPatient: selectPatientProp,
  setNewPatientModal: setNewPatientModalProp,
  disclaimersAndCheckboxes,
  disclaimerMessage,
  selectedVertical,
  reimbursement,
}) => {
  const user = useSelector((state) => state.user || {});

  const self = useMemo(() => {
    const isOffline = [
      SERVICE_LINE_NAMES.OFFLINE,
      SERVICE_LINE_NAMES.PREBOOK,
    ].includes(serviceLine);

    return isOffline
      ? {
          relativeAge: user?.userAge,
          relativeDob: user?.dateOfBirth,
          relativeGender: user?.userGender,
          relativeId: 'self',
          relativeName: user?.userName,
          relativePhone: user?.userPhone,
          relativeRelation: user?.userType,
        }
      : {
          age: user?.userAge,
          dob: user?.dateOfBirth,
          gender: user?.userGender,
          id: 'self',
          name: user?.userName,
          phone: user?.userPhone,
          relation: user?.userType,
        };
  }, [user, serviceLine]);

  const [showPatients, setShowPatients] = useState(true);
  const [showNewPatient, setShowNewPatient] = useState(!!showPatient);
  const [updateAddress, setUpdateAddress] = useState({});
  const [userName, setUserName] = useState('');
  const [userAge, setUserAge] = useState('');
  const [userContact, setUserContact] = useState('');
  const [userGender, setUserGender] = useState('');
  const [editUser, setEditUser] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rawResponse, setRawResponse] = useState(null);
  const [skipPatientModal, setSkipPatientModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(
    initialSelectedPatient
  );
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);

  const nodeRef = useRef(null);
  const node2Ref = useRef(null);

  const fetchPatient = async () => {
    console.log('serviceLine', serviceLine);
    let res = null;
    if (serviceLine === SERVICE_LINE_NAMES.VISION) {
      res = await PATIENT_MODAL_CONFIG[serviceLine].getPatient(serviceLine);
    } else {
      res = await PATIENT_MODAL_CONFIG[serviceLine].getPatient(
        cartId || null,
        isConciergeBooking || null
      );
    }

    console.log('response of api', res);

    if (res.message === 'success') {
      const temp =
        [...res[PATIENT_MODAL_CONFIG[serviceLine]?.responseKey]] || [];

      // if (PATIENT_MODAL_CONFIG[serviceLine]?.addSelfManually) {
      //   if (res?.showSelfUser !== undefined && res?.showSelfUser !== 1) {
      //     // Don't add self for TELE if showSelfUser exists and is not 1
      //     // Do nothing
      //   } else {
      //     // Add self for:
      //     // - TELE where showSelfUser is 1/0/undefined
      //     // - all other service lines
      //     temp.unshift(self);
      //   }
      // }
      if (res?.showSelfUser) {
        console.log('res?.showSelfUser');
        temp.unshift(self);
      }

      setPatients(temp);
      setLoading(false);
      setRawResponse(res);
      setShowAddPatient(res?.allowAddition || false);
      setSkipPatientModal(res?.skipPatientSelection || false);
    }
  };

  useEffect(() => {
    fetchPatient();
    logEvent(serviceLine + ' Choose Patient Screen');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipPatientModal) {
      selectPatientProp(null, skipPatientModal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipPatientModal]);

  const hideModal = () => {
    if (hideModalProp && !showNewPatient) hideModalProp();
    if (showNewPatient) {
      setShowPatients(true);
      setShowNewPatient(false);
      setUserName('');
      setUserAge('');
      setUserContact('');
      setUserGender('');
      setEditUser(null);
      return;
    }
    setShowModal(false);
  };

  const handleOutsideClick = (e) => {
    // ignore clicks on the component itself
    if (
      (nodeRef.current && nodeRef.current.contains(e.target)) ||
      (node2Ref.current && node2Ref.current.contains(e.target))
    ) {
      return;
    }
    if (hideModalProp) return hideModal();
    if (showNewPatient) {
      setShowPatients(true);
      setShowNewPatient(false);
      setUserName('');
      setUserAge('');
      setUserContact('');
      setUserGender('');
      setEditUser(null);
      return;
    }
    hideModal();
  };

  const addNewPatientModal = (patientUser = null) => {
    setShowNewPatient(true);
    setEditUser(patientUser);
    if (setNewPatientModalProp) setNewPatientModalProp();
    if (patientUser) {
      setUserName(patientUser.name);
      setUserAge(patientUser.age);
      setUserContact(patientUser.phone);
      setUserGender(patientUser.gender);
    }
  };

  const saveNewPatient = () => {
    setSaving(true);
    const patientDetails = {
      name: userName,
      age: userAge,
      phone: userContact,
      gender: userGender,
    };

    if (editUser) {
      patientDetails.patientId = editUser.patientId;

      PATIENT_MODAL_CONFIG[serviceLine]
        .editFunction(patientDetails)
        .then((res) => {
          setEditUser(null);
          fetchPatient();
          hideModal();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {});
    } else {
      PATIENT_MODAL_CONFIG[serviceLine]
        .addFunction(patientDetails)
        .then((res) => {
          logEvent(serviceLine + 'Patient Added', {
            screenName: serviceLine + ' Add Patient Screen',
            category: serviceLine,
            patientId: res.patientId,
            patientName: patientDetails.name,
          });
          setEditUser(null);
          fetchPatient();
          hideModal();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {});
    }
  };

  if (skipPatientModal) return null;

  const getVerticalDisclaimer = () => {
    return disclaimersAndCheckboxes?.filter(
      (d) => d.verticalId === selectedVertical
    )[0]?.message;
  };

  const renderPatient = (ele) => {
    const getPatientIdentifier = (patient) =>
      patient?.UHID ?? patient?.id ?? patient?.patientId ?? patient?.relativeId;

    const isSelected =
      selectedPatient &&
      getPatientIdentifier(ele) === getPatientIdentifier(selectedPatient);

    const isNotSelected = selectedPatient
      ? getPatientIdentifier(ele) !== getPatientIdentifier(selectedPatient)
      : true;

    let selected = false;
    if (selectedPatient?.UHID) {
      // reimbursement flow
      selected = ele?.UHID === selectedPatient?.UHID;
    } else if (selectedPatient?.patientId) {
      selected = ele.patientId === selectedPatient?.patientId;
    }

    return (
      <div
        className="flex flex-row justify-between items-center rounded-12 bg-primary-whiteBackground mb-8px py-24px px-20px"
        key={ele?.UHID || ele?.id}
      >
        <button
          onClick={() => {
            logEvent(serviceLine + ' Patient Selected', {
              patientId: ele?.relativeId || ele?.relativeId,
              patientName: ele?.name || ele?.relativeName,
              category: serviceLine,
              screenName: 'CHOOSE_PATIENT_SCREEN',
            });
            if (cartId && PATIENT_MODAL_CONFIG[serviceLine]?.selectPatient) {
              PATIENT_MODAL_CONFIG[serviceLine].selectPatient(
                cartId,
                ele.patientId
              );
            }
            setSelectedPatient(ele);
            selectPatientProp(ele);
          }}
          className="w-full flex flex-row justify-start items-start"
        >
          {/* {isSelected && (
            <svg
              width="22"
              height="22"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.18898 17.2583C13.8093 17.2583 17.5548 13.5611 17.5548 9.00025C17.5548 4.43945 13.8093 0.742188 9.18898 0.742188C4.56864 0.742188 0.82312 4.43945 0.82312 9.00025C0.82312 13.5611 4.56864 17.2583 9.18898 17.2583Z"
                fill="var(--primary)"
              />
            </svg>
          )} */}
          <div
            className="mr-12px rounded-full h-40px w-40px bg-primary-whiteBackground"
            style={{
              border: isSelected ? '2px solid #714FFF' : '1px solid #EBEAF5',
            }}
          >
            {ele?.icon && <img src={ele.icon} alt="patient" />}
          </div>
          {/* {isNotSelected && (
            <svg
              width="18"
              height="17"
              viewBox="0 0 18 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M9.03045 16.8943C13.6238 16.8943 17.3475 13.2304 17.3475 8.71082C17.3475 4.19121 13.6238 0.527344 9.03045 0.527344C4.43706 0.527344 0.713379 4.19121 0.713379 8.71082C0.713379 13.2304 4.43706 16.8943 9.03045 16.8943Z"
                fill="#D8D8D8"
              />
            </svg>
          )} */}
          <div className="txt-black-600-12 text-body-s font-semibold flex flex-col justify-start items-start">
            <span>{ele?.name || ele?.relativeName}</span>
            {(ele?.gender || ele?.relativeGender) && (
              <span>
                {ele?.age || ele?.relativeAge},
                {ele?.gender || ele?.relativeGender}
              </span>
            )}
            {!ele?.UHID && (
              <span className="txt-grey-10">
                {ele?.phone || ele?.relativePhone}
              </span>
            )}
            {reimbursement && ele?.UHID && (
              <span className="txt-grey-10">{ele?.UHID}</span>
            )}
          </div>
        </button>
        {PATIENT_MODAL_CONFIG[serviceLine]?.showEditPatient && (
          <div className="flex flex-row items-center">
            <button
              onClick={() => {
                addNewPatientModal(ele);
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="32" rx="8" fill="#F0F3F6" />
                <path
                  opacity="0.01"
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M1 1V31H31V1H1Z"
                  fill="#1C8FF8"
                  fill-opacity="0.338428"
                />
                <path
                  d="M18.3891 11.0186C18.5822 10.8348 18.8856 10.8348 19.0787 11.0186L20.2301 12.1149C20.4371 12.312 20.4371 12.6421 20.2301 12.8392L14.1257 18.651C14.0327 18.7395 13.9093 18.7889 13.7809 18.7889H12.5791C12.303 18.7889 12.0791 18.565 12.0791 18.2889V17.2405C12.0791 17.1037 12.1352 16.9728 12.2343 16.8784L18.3891 11.0186Z"
                  stroke="#3F8CFF"
                  stroke-linecap="round"
                  stroke-linejoin="bevel"
                />
                <path
                  d="M12.0845 22H21.6845"
                  stroke="#3F8CFF"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal>
      <div className="modalContainer" onClick={handleOutsideClick}>
        <FlyInTransition
          in={showPatients}
          mountOnEnter
          unmountOnExit
          onExited={hideModal}
          appear
        >
          <div className="w-full modalBody flex items-center" ref={nodeRef}>
            <div className="modalHeader w-full">
              <div className="flex flex-row items-center w-full gap-2">
                <img src={profileIcon} alt="" />
                <p className="text-body-l text-modalTextColor font-semibold">
                  Choose Patient
                </p>
              </div>
              <CloseContainer onTap={hideModal}>
                <img src={crossIcon} alt="" />
              </CloseContainer>
            </div>
            <div className="w-full modalDetails bg-primary-lightShade bg-opacity-50">
              {!loading ? (
                <div className="flex flex-col gap-4">
                  {disclaimerMessage && getVerticalDisclaimer() ? (
                    <DisclaimerText>{getVerticalDisclaimer()}</DisclaimerText>
                  ) : (
                    ''
                  )}
                  {disclaimerMessage && (
                    <DisclaimerText>{disclaimerMessage}</DisclaimerText>
                  )}
                  {patients && patients.map(renderPatient)}
                  {showAddPatient && (
                    <div
                      className="flex flex-row items-center justify-center mt-20px"
                      onClick={() => {
                        logEvent(serviceLine + 'Add Patient');
                        addNewPatientModal();
                      }}
                    >
                      <div className="bg-primary-whiteBackground p-6px bg-opacity-40 rounded-6">
                        <AddTextIcon />
                      </div>
                      <span className="text-body-l text-primary font-semibold pl-10px">
                        Add new patient
                      </span>
                    </div>
                  )}

                  {/* <div className="mt-24px">
                    <div
                      className="pl-16px pr-16px mt-12px rounded-12 hover pt-12px pb-12px text-button-s font-semibold text-textbox text-center flex flex-row justify-center items-center"
                      style={{
                        background: selectedPatient ? '#714fff' : '#e3e3e3',
                        backdropFilter: 'blur(4px)',
                        width: '100%',
                      }}
                      onClick={() => {
                        this.props.selectPatient(this.state.selectedPatient);
                      }}
                    >
                      <span className="flex flex-row items-center">
                        <p className="mr-8px text-body-l font-semibold text-textbox">
                          Next
                        </p>
                      </span>
                    </div>
                  </div> */}
                </div>
              ) : (
                <>
                  <LoadingComponent />
                </>
              )}
            </div>
          </div>
        </FlyInTransition>
        <FlyInTransition
          in={showNewPatient}
          mountOnEnter
          unmountOnExit
          onExited={hideModal}
          appear
        >
          <div className="modalBody" ref={node2Ref}>
            <div className="modalHeader">
              <div className="flex flex-row items-center">
                <img src={profileIcon} alt="" />
                <p className="ml-10px text-body-l text-txtlight-new font-semibold">
                  Add Patient
                </p>
              </div>
              <CloseContainer onTap={hideModal}>
                <CloseIcon />
              </CloseContainer>
            </div>
            <div className="modalDetails" style={{}}>
              <div>
                <p className="text-body-s text-txtlight font-medium">
                  Full Name
                </p>
                <input
                  className="mt-8px new-address-input text-title-s font-semibold"
                  placeholder="Full Name"
                  value={userName}
                  onChange={(e) => {
                    setUserName(e.target.value);
                  }}
                />
              </div>
              <div className="mt-24px">
                <p className="text-body-s text-txtlight font-medium">Age</p>
                <input
                  type="number"
                  className="mt-8px new-address-input text-title-s font-semibold"
                  placeholder="Age*"
                  value={userAge}
                  onChange={(e) => {
                    setUserAge(e.target.value);
                  }}
                />
              </div>

              <div className="mt-24px">
                <p className="text-body-s text-txtlight font-medium">Gender</p>
                <div className="flex flex-row w-full justify-start mt-8px">
                  <span
                    className="text-center relative pt-8px pb-8px border border-txtsecondary-shades7 border-solid rounded-12 pr-24px pl-24px mr-24px"
                    onClick={() => {
                      setUserGender('Male');
                    }}
                    style={
                      userGender === 'Male' ? { borderColor: '#6C5DD3' } : {}
                    }
                  >
                    <p
                      className="text-body-l font-regular"
                      style={
                        userGender === 'Male'
                          ? { color: '#6C5DD3', fontWeight: '600' }
                          : { color: '#302E6B' }
                      }
                    >
                      Male
                    </p>
                    {userGender === 'Male' && (
                      <span className="absolute -top-10px -right-10px">
                        <img src={checkedIcon} alt="tick" />
                      </span>
                    )}
                  </span>
                  <span
                    className="text-center relative pt-8px pb-8px border border-txtsecondary-shades7 border-solid rounded-12 pr-24px pl-24px"
                    onClick={() => {
                      setUserGender('Female');
                    }}
                    style={
                      userGender === 'Female' ? { borderColor: '#6C5DD3' } : {}
                    }
                  >
                    <p
                      className="text-body-l font-regular"
                      style={
                        userGender === 'Female'
                          ? { color: '#6C5DD3', fontWeight: '600' }
                          : { color: '#302E6B' }
                      }
                    >
                      Female
                    </p>
                    {userGender === 'Female' && (
                      <span className="absolute -top-10px -right-10px">
                        <img src={checkedIcon} alt="tick" />
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="mt-24px">
                <p className="text-body-s text-txtlight font-medium">
                  Contact Number
                </p>
                <input
                  type="number"
                  className="mt-8px new-address-input text-title-s font-semibold"
                  placeholder="contact*"
                  value={userContact}
                  onChange={(e) => {
                    setUserContact(e.target.value);
                  }}
                />
              </div>
              <div className="mt-24px">
                <div
                  className="pl-16px pr-16px mt-12px rounded-12 hover pt-12px pb-12px text-button-s font-semibold text-textbox text-center flex flex-row justify-center items-center"
                  style={{
                    background: '#6C5DD3',
                    backdropFilter: 'blur(4px)',
                    width: '100%',
                  }}
                  onClick={saveNewPatient}
                >
                  <span className="flex flex-row items-center">
                    <p className="mr-8px text-body-l font-semibold text-textbox">
                      Next
                    </p>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </FlyInTransition>
      </div>
    </Modal>
  );
};

export default PatientModal;
